import * as Sentry from "@sentry/react";
import { ApiError, NetworkError } from "@/lib/query-client";

/**
 * Rate limit: drop a repeat of the same error message seen within this window.
 * One failing endpoint (retried, or surfaced through both apiRequest and
 * useMutation's onError) should not flood Sentry with identical events.
 */
const DEDUP_WINDOW_MS = 5000;
const recentlyCaptured = new Map<string, number>();

function dedupeKey(error: unknown, err: Error): string {
  if (error instanceof ApiError) return `api:${error.status}:${err.message}`;
  return `${err.name}:${err.message}`;
}

/**
 * Returns true if an identical message was captured within the window. On a
 * miss it records the timestamp and prunes stale entries so the map cannot
 * grow unbounded across a long session.
 */
function isRateLimited(key: string): boolean {
  const now = Date.now();
  const last = recentlyCaptured.get(key);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) return true;
  recentlyCaptured.forEach((t, k) => {
    if (now - t >= DEDUP_WINDOW_MS) recentlyCaptured.delete(k);
  });
  recentlyCaptured.set(key, now);
  return false;
}

/** Exported for tests — clears the rate-limit state between cases. */
export function _resetCaptureRateLimit(): void {
  recentlyCaptured.clear();
}

export function captureError(
  error: unknown,
  extra?: Record<string, unknown>
): void {
  // Form validation errors are user-caused — skip (and leave _sentryReported
  // false so nothing treats them as reported).
  if (
    error instanceof ApiError &&
    (error.status === 422 || error.code === "VALIDATION_ERROR")
  ) {
    return;
  }

  // Expired-session 401s are expected churn, not actionable signal. Skip them
  // entirely (we no longer report them even at info level); handleAuthError
  // drives the re-login UX. Mark as reported so useMutation's onError fallback
  // does not re-enter and attempt to capture them.
  if (error instanceof ApiError && error.status === 401) {
    error._sentryReported = true;
    return;
  }

  const err = error instanceof Error ? error : new Error(String(error));

  // Mark ApiError / NetworkError as reported BEFORE the rate-limit check so
  // that even a throttled duplicate is flagged — useMutation's onError path
  // (which re-captures by default) then skips it instead of double-reporting.
  if (error instanceof ApiError || error instanceof NetworkError) {
    error._sentryReported = true;
  }

  if (isRateLimited(dedupeKey(error, err))) {
    return;
  }

  if (error instanceof NetworkError) {
    Sentry.captureException(err, {
      tags: { error_type: "network" },
      extra,
    });
    return;
  }

  if (error instanceof ApiError) {
    Sentry.captureException(err, {
      extra: { status: error.status, code: error.code, ...extra },
    });
    return;
  }

  Sentry.captureException(err, { extra });
}
